import React, { useState, useEffect, useRef } from 'react'
import { Box, TextField, Typography, Link, Button, IconButton, CircularProgress } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import Alert from '@material-ui/lab/Alert'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import Info from '@material-ui/icons/Info'
import LocationOnOutlined from '@material-ui/icons/LocationOnOutlined'
import Apartment from '@material-ui/icons/Apartment'
import { validateCups } from '../../../services/addressFinder/validators'
import { searchAddressAutocomplete, getAddressDetails, getVerticalDetail, getTechnicalInfo, searchByCups, getCalculatorData } from '../../../services/addressFinder/api'
import useStyles from './styles'
import { useSelector } from 'react-redux'
import { getSelectedRate } from 'services/products/selectors'
import { getSelectedTenure } from 'services/customer/selectors'
import isEmptyObject from '../../../utils/isEmptyObject'
import { BOTH } from '../../../utils/constants'
import { useTranslation, Trans } from 'react-i18next'

/**
 * Compara los últimos dígitos visibles de dos CUPS
 * @param {string} cups1 - CUPS original (puede estar completo)
 * @param {string} cups2 - CUPS enmascarado (ej: ES0021******584371GP)
 * @returns {boolean} - true si los últimos dígitos coinciden
 */
const compareCupsLastDigits = (cups1, cups2) => {
  if (!cups1 || !cups2) return false

  // Extraer los últimos dígitos visibles del CUPS enmascarado
  // Formato típico: ES0021******584371GP
  // Queremos extraer "584371GP" (últimos 8 caracteres antes de posibles sufijos)
  const extractVisibleDigits = (cups) => {
    // Eliminar asteriscos y espacios
    const cleaned = cups.replace(/\*/g, '').replace(/\s/g, '')
    // Tomar los últimos 8 caracteres (normalmente son los dígitos finales + GP)
    return cleaned.slice(-8)
  }

  const digits1 = extractVisibleDigits(cups1)
  const digits2 = extractVisibleDigits(cups2)

  return digits1 === digits2
}

/**
 * AddressSearch Component
 * 
 * Componente reutilizable para búsqueda de CUPS o dirección
 * Callejero de la aplicación
 * 
 * @param {Object} props
 * @param {string} props.mode - Modo inicial: 'cups' o 'address'
 * @param {string} props.category - Categoría: 'electricity', 'gas', 'none'
 * @param {string} props.energy - Tipo de energía de la contratación: 'electricity', 'gas', 'gasAndElectricity' (si no se pasa, usa category)
 * @param {number} props.step - Paso del proceso
 * @param {string} props.leadId - ID del lead (requerido para llamar a getVerticalDetail)
 * @param {string} props.cups - CUPS inicial (opcional)
 * @param {Function} props.onFinish - Callback cuando se completa la búsqueda
 * @param {Function} props.onModeChange - Callback cuando cambia el modo
 * @param {boolean} props.showModeToggle - Mostrar enlace para cambiar modo
 * @param {Object} props.translations - Traducciones personalizadas
 * @param {Function} props.openCupsModal - Callback para abrir modal de información CUPS
 */
const AddressSearch = ({
  mode: initialMode = 'cups',
  category = 'electricity',
  energy,
  step = 0,
  leadId,
  cups: initialCups = '',
  onFinish,
  onModeChange,
  showModeToggle = true,
  translations: customTranslations = {},
  openCupsModal,
  autoSubmit = false,
  hideTitle = false,
  showErrorModal,
  ownerElectricityAlert,
  ownerGasAlert,
  showChangeOwnerModal,
  edit = false
}) => {

  const classes = useStyles()
  const { t } = useTranslation()
  // Si no se pasa energy, usar category por defecto
  const selectedProduct = useSelector(getSelectedRate)
  const energyType = energy || category
  const isProcessingNumberRef = useRef(false);
  const shouldShowCupsSearch = step !== 'billing' && step !== 5
  const [mode, setMode] = useState(shouldShowCupsSearch ? initialMode : 'address')
  const [cupsValue, setCupsValue] = useState(initialCups)
  const [addressValue, setAddressValue] = useState('')
  const [error, setError] = useState('')
  const [isValid, setIsValid] = useState(false)
  const [showCupsNotFoundAlert, setShowCupsNotFoundAlert] = useState(false)
  const [initialFailedCups, setInitialFailedCups] = useState('') // CUPS que falló inicialmente

  // Estados para autocomplete de direcciones
  const [addressOptions, setAddressOptions] = useState([])
  const [selectedAddress, setSelectedAddress] = useState(null)
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showCupsHelper, setShowCupsHelper] = useState(false)
  const [helperCupsValue, setHelperCupsValue] = useState('')
  const [isHelperCupsValid, setIsHelperCupsValid] = useState(false)
  const [showNoResultsOption, setShowNoResultsOption] = useState(false)
  const [helperCupsSearchFailed, setHelperCupsSearchFailed] = useState(false)
  const [showAddressSearchError, setShowAddressSearchError] = useState(false)
  
  // Estados para campo de número (error código 13 - Not Number)
  const [showNumberField, setShowNumberField] = useState(false)
  const [numberValue, setNumberValue] = useState('')
  const [addressWithoutNumber, setAddressWithoutNumber] = useState(null)

  // Estados para verticales (piso, puerta, etc.)
  const [verticals, setVerticals] = useState([])
  const [verticalsDetail, setVerticalsDetail] = useState([])
  const [selectedVertical, setSelectedVertical] = useState('')
  const [isLoadingVerticals, setIsLoadingVerticals] = useState(false)
  const throttleTimerRef = useRef(null)
  const lastSearchTimeRef = useRef(0)
  const noResultsTimerRef = useRef(null)
  const THROTTLE_DELAY = 300 // ms entre búsquedas
  const NO_RESULTS_DELAY = 200 // ms antes de mostrar "No encuentro mi dirección" (justo después del loading)

  // Traducciones por defecto
  const defaultTranslations = {
    cups: {
      title: 'Te recomendamos tener a mano una factura para encontrar tu CUPS, un código de 20/22 dígitos que identifica tus puntos de suministro, ¿no lo encuentras?',
      placeholder: category === 'electricity' ? 'CUPS de luz' : category === 'gas' ? 'CUPS de gas' : 'CUPS',
      toggleLink: 'introducir tu dirección',
      toggleText: 'Si lo prefieres puedes',
      button: step === 0 ? 'Calcular' : 'Comprobar',
      invalidFormat: 'Repasa el CUPS. Formato incorrecto.',
    },
    address: {
      title: 'Buscar CUPS por dirección',
      subtitle: 'No te preocupes, podemos localizarlo. Introduce tu dirección.',
      placeholder: 'Busca tu dirección (tipo vía, nombre, nº, C.P., localidad)',
      toggleLink: 'introducir el CUPS',
      toggleText: 'Si lo prefieres puedes',
      button: step === 0 ? 'Calcular' : 'Comprobar',
      noResults: 'No se encuentra la dirección.',
    },
  }

  const translations = {
    cups: { ...defaultTranslations.cups, ...customTranslations.cups },
    address: { ...defaultTranslations.address, ...customTranslations.address },
  }

  // Sincronizar initialCups con cupsValue cuando cambia (por ejemplo, después de guardar en Redux)
  useEffect(() => {

    // Si initialCups tiene valor, actualizarlo
    if (initialCups) {
      setCupsValue(initialCups)
      // Si el CUPS es válido, validarlo automáticamente
      if (validateCups(initialCups, category)) {
        setIsValid(true)
        setError('')
      }
    } else {
      // Si initialCups está vacío, SIEMPRE resetear (esto cubre el caso de edición)
      setCupsValue('')
      setAddressValue('')
      setIsValid(false)
      setError('')
      setShowCupsNotFoundAlert(false)
      setAddressOptions([])
      setSelectedAddress(null)
      setVerticals([])
      setSelectedVertical('')
      setShowCupsHelper(false)
      setHelperCupsValue('')
      setIsHelperCupsValid(false)
      setShowAddressSearchError(false)
      setShowNumberField(false)
      setNumberValue('')
      setAddressWithoutNumber(null)
    }
  }, [initialCups, category, edit])

  const handleCupsBlur = () => {
    if (!cupsValue || cupsValue.trim() === '') {
      setError('')
      return
    }

    const validation = validateCups(cupsValue, category)
    // Soporta tanto si devuelve boolean como si devuelve un objeto { isValid: boolean }
    const isCupsValid = typeof validation === 'boolean' ? validation : validation?.isValid

    if (!isCupsValid) {
      setError(translations.cups.invalidFormat)
    } else {
      setError('')
    }
  }

  const handleModeToggle = () => {
    const newMode = mode === 'cups' ? 'address' : 'cups'
    setMode(newMode)
    setError('')
    setAddressValue('')
    setIsValid(false)

    // NO activar showCupsHelper automáticamente al cambiar de modo
    // Solo debe mostrarse cuando el usuario hace click en "No encuentro mi dirección"
    // Pero sí guardar el CUPS para usarlo después si es necesario
    if (newMode === 'address' && cupsValue) {
      setHelperCupsValue(cupsValue)
      // Validar el CUPS guardado para que isHelperCupsValid sea true
      const validation = validateCups(cupsValue, category)
      setIsHelperCupsValid(validation.isValid)
    }

    // Siempre limpiar showCupsHelper al cambiar de modo
    setShowCupsHelper(false)

    // Limpiar opciones de dirección
    setAddressOptions([])
    setSelectedAddress(null)
    setShowNoResultsOption(false)
    // Limpiar alerta de CUPS no encontrado
    setShowCupsNotFoundAlert(false)

    if (onModeChange) {
      // Pasar el modo y el cupsValue actual al componente padre
      onModeChange(newMode, cupsValue)
    }
  }

  const handleCupsChange = (event) => {
    const value = event.target.value.toUpperCase().replace(/\s/g, '')
    setCupsValue(value)
    setError('')
    setShowCupsNotFoundAlert(false) // Limpiar alerta al cambiar el CUPS

    if (value.length === 0) {
      setIsValid(false)
      return
    }

    const validation = validateCups(value, category)
    setIsValid(validation.isValid)

    if (!validation.isValid && value.length >= 20) {
      setError(translations.cups.invalidFormat)
    }
  }

  // Función para buscar direcciones con debounce
  const searchAddresses = async (inputValue) => {

    if (!inputValue || inputValue.length < 3) {
      setAddressOptions([])
      setIsLoadingAddresses(false)
      return
    }

    setIsLoadingAddresses(true)

    // Calcular el step correcto: en paso 2 del funnel, el step de la API es 3
    const apiStep = step === 2 ? 3 : 0


    try {
      const result = await searchAddressAutocomplete({
        input: inputValue,
        category,
        step: apiStep,
      })


      if (result.success && result.data?.predictions && result.data.predictions.length > 0) {
        setAddressOptions(result.data.predictions)
        setShowNoResultsOption(false)
        // Limpiar timer de "No encuentro mi dirección"
        if (noResultsTimerRef.current) {
          clearTimeout(noResultsTimerRef.current)
        }
      } else {
        // Si no hay resultados, esperar 2 segundos antes de mostrar "No encuentro mi dirección"
        setAddressOptions([])
        setShowNoResultsOption(false)

        // Limpiar timer anterior si existe
        if (noResultsTimerRef.current) {
          clearTimeout(noResultsTimerRef.current)
        }

        // Programar mostrar "No encuentro mi dirección" después de 2 segundos
        noResultsTimerRef.current = setTimeout(() => {
          setShowNoResultsOption(true)
        }, NO_RESULTS_DELAY)
      }
    } catch (err) {
      console.error('[NATLOC] Error searching addresses:', err)
      setAddressOptions([])
      setShowNoResultsOption(false)
    } finally {
      // Pequeño delay antes de quitar el loading para evitar parpadeo
      setTimeout(() => {
        setIsLoadingAddresses(false)
      }, 100)
    }
  }

  const handleAddressInputChange = (event, newInputValue) => {
    // Si estamos procesando el número, ignorar cambios en el input
    if (isProcessingNumberRef.current) {
      return
    }
    
    setAddressValue(newInputValue)
    setError('')

    // Limpiar dirección seleccionada y verticales cuando cambia el input
    setVerticals([])
    setSelectedVertical('')
    setIsValid(false)

    // Si el input está vacío, limpiar opciones
    if (!newInputValue || newInputValue.length < 3) {
      setAddressOptions([])
      setIsLoadingAddresses(false)
      return
    }

    // Implementar throttle: lanzar búsqueda de forma escalonada
    const now = Date.now()
    const timeSinceLastSearch = now - lastSearchTimeRef.current

    if (timeSinceLastSearch >= THROTTLE_DELAY) {
      // Ha pasado suficiente tiempo, lanzar búsqueda inmediatamente
      lastSearchTimeRef.current = now
      searchAddresses(newInputValue)
    } else {
      // Aún no ha pasado suficiente tiempo, programar búsqueda
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current)
      }

      const remainingTime = THROTTLE_DELAY - timeSinceLastSearch

      throttleTimerRef.current = setTimeout(() => {
        lastSearchTimeRef.current = Date.now()
        searchAddresses(newInputValue)
      }, remainingTime)
    }
  }

  const handleNumberChange = (event) => {
    const value = event.target.value
    setNumberValue(value)
  }

  const insertNumberBeforeFirstComma = (originalDescription, number) => {
    if (!originalDescription || !number) return originalDescription;

    // Inserta el número seguido de una coma antes de la primera coma existente
    return originalDescription.replace(',', `, ${number},`);
  }

  const handleAddressSelect = async (event, newValue, skipNumberCheck = false) => {

      // Detectar si seleccionó "No encuentro mi dirección."
      if (newValue?.isNoResultsOption || (typeof newValue === 'string' && newValue.includes('No encuentro mi dirección'))) {
        // Solo mostrar el helper de CUPS si shouldShowCupsSearch es true
        if (shouldShowCupsSearch) {
          setShowCupsHelper(true)
          setHelperCupsValue('')
          setIsHelperCupsValid(false)
          setHelperCupsSearchFailed(false)
        } else {
          // En billing step, mostrar directamente el warning para abrir modal manual
          setShowAddressSearchError(true)
        }
        setSelectedAddress(null)
        setIsValid(false)
        setVerticals([])
        setSelectedVertical('')
        // Mantener el valor del input y cerrar el dropdown
        setAddressOptions([])
        return
      }

    setSelectedAddress(newValue)
    setShowCupsHelper(false)
    setShowAddressSearchError(false) // Limpiar error al seleccionar dirección

    // Si se seleccionó una dirección válida, obtener las verticales
    // PRIORIDAD: placeId tiene prioridad sobre uuid
    if (newValue && (newValue.placeId || newValue.uuid) && newValue.description) {
      const placeIdFromAutocomplete = newValue?.placeIDGoogle || newValue?.placeId
      const uuidFromAutocomplete = newValue?.uuid
      setIsLoadingVerticals(true)
      setVerticals([])
      setSelectedVertical('')
      setIsValid(false) // No es válido hasta que seleccione una vertical

      try {
        // La API solo acepta step 0, 2 o 3
        // step 0: calculadora inicial
        // step 2: paso 2 del funnel
        // step 3: paso 3 del funnel (datos técnicos)
        const apiStep = step === 2 ? 3 : step === 0 ? 0 : 2


        const result = await getAddressDetails({
          placeId: newValue.placeId,
          uuid: uuidFromAutocomplete,
          label: newValue.description,
          category,
          step: apiStep,
        })

        
        
        // Verificar si la API devolvió error código 13 (Not Number)
        // El error puede venir en result.errorCode O en result.data.results.code
        const isNotNumber = !result.success && (
          (result.errorCode === '13' && result.error === 'Not Number') ||
          (result.data?.results?.code === '13' && result.data?.results?.message === 'Not Number')
        )


        // Si es error "Not Number" y no estamos en un reintento, mostrar campo de número
        if (isNotNumber) {
          setShowNumberField(true)
          setAddressWithoutNumber(newValue)
          setSelectedAddress(newValue) // Mantener la dirección seleccionada
          setIsValid(false)
          // NO limpiar verticales aquí, solo indicar que falta el número
          setIsLoadingVerticals(false)
          return
        } else {
          setShowNumberField(false)
          setNumberValue('') // Limpiar el valor del número si no es necesario
        }
        const isHorizontalNotFound = result.data?.code === 200 && 
                                      result.data?.status === 'error' && 
                                      result.data?.results?.code === '12' &&
                                      result.data?.results?.message === 'Horizontal Not Found'
        // Verificar si la API devolvió un error
        if (!result.success  || isHorizontalNotFound) {
          // Si falla la búsqueda de verticales, mostrar alerta de error
          if (isHorizontalNotFound) {
            if (shouldShowCupsSearch) {
              setShowCupsHelper(true)
              setHelperCupsValue('')
              setIsHelperCupsValid(false)
              setHelperCupsSearchFailed(false)
            } else {
              // En billing step sin CUPS search, mostrar warning de modal
              setShowAddressSearchError(true)
            }
          } else {
          setShowAddressSearchError(true)
          if (shouldShowCupsSearch) {
            setShowCupsHelper(true)
            setHelperCupsValue('')
            setIsHelperCupsValid(false)
            setHelperCupsSearchFailed(false)
          }
        }
          // En billing step, el warning ya se muestra con showAddressSearchError
          setError('')
          setIsValid(false)
        } else if (result.data?.verticals && result.data.verticals.length > 0) {
          setVerticals(result.data.verticals)
          // Añadir placeIDGoogle y description a verticalsDetail (usar el valor directo de newValue, no el estado)
          setVerticalsDetail({
            ...result.data,
            placeIDGoogle: placeIdFromAutocomplete,
            description: result.data?.niceAddress || newValue?.description || newValue?.label || '',
          })

          // Si solo hay 1 vertical y todos sus campos están vacíos (dirección única), seleccionarla automáticamente
          if (result.data.verticals.length === 1) {
            const vertical = result.data.verticals[0]
            const hasAnyField = vertical.building || vertical.staircase || vertical.floor || vertical.door

            if (!hasAnyField && vertical.value) {
              // Seleccionar automáticamente la vertical única vacía
              setSelectedVertical(vertical.value)
              setIsValid(true)
            } else {
              // Si tiene campos, el usuario debe seleccionarla manualmente
              setIsValid(false)
            }
          } else {
            // Si hay múltiples verticales, el usuario debe seleccionar una
            setIsValid(false)
          }
        } else {
          // Si no hay verticales, la dirección es válida tal cual
          setIsValid(true)
        }
      } catch (err) {
        // Si falla la búsqueda de verticales, mostrar alerta de error
        setSelectedAddress(null)
        setShowAddressSearchError(true)
        if (shouldShowCupsSearch) {
          setShowCupsHelper(true)
          setHelperCupsValue('')
          setIsHelperCupsValid(false)
          setHelperCupsSearchFailed(false)
        }
        // En billing step, el warning ya se muestra con showAddressSearchError
        setError('')
        setIsValid(false)
      } finally {
        setIsLoadingVerticals(false)
      }
    } else {
      setIsValid(false)
      setVerticals([])
      setSelectedVertical('')
      // Solo mostrar el helper de CUPS si shouldShowCupsSearch es true
      if (shouldShowCupsSearch) {
        setShowCupsHelper(true)
        setHelperCupsValue('')
        setIsHelperCupsValid(false)
        setHelperCupsSearchFailed(false)
      } else {
        // En billing step, mostrar el warning para abrir modal manual
        setShowAddressSearchError(true)
      }
    }
  }

  const handleHelperCupsChange = (event) => {
    const value = event.target.value.toUpperCase().replace(/\s/g, '')
    setHelperCupsValue(value)
    setHelperCupsSearchFailed(false) // Limpiar el estado de error al cambiar el CUPS

    if (value.length === 0) {
      setIsHelperCupsValid(false)
      return
    }

    const validation = validateCups(value, category)
    // Validar solo el formato del CUPS, no requerir leadId aquí
    const isCupsValid = typeof validation === 'boolean' ? validation : validation?.isValid
    setIsHelperCupsValid(isCupsValid)

  }

  // Nueva función para comprobar el CUPS del helper
  const handleHelperCupsSubmit = async () => {

    if (!isHelperCupsValid || !helperCupsValue) {
      return
    }

    setIsLoadingVerticals(true)

    try {
      const apiStep = step === 2 ? 3 : step === 0 ? 0 : 2

      const addressResult = await searchByCups({
        cups: helperCupsValue,
        leadId: leadId ?? null,
        category,
        step: apiStep,
        energy: energyType,
      })


      // Si el CUPS no se encuentra (404), marcar como fallido y mostrar alerta
      if (!addressResult.success && (addressResult?.httpCode === 404 || addressResult?.errorCode === '02')) {
        setHelperCupsSearchFailed(true)
        setShowAddressSearchError(true)
        setIsLoadingVerticals(false)
        return
      } else if (addressResult.success) {
        // Si el CUPS se encuentra, procesar igual que handleSubmit
        const addressId = addressResult?.data?.ADDRESS_ID

        // ========== BILLING ADDRESS (sin technical info) ==========
        // step 5 = billing step (STEPS.billing)
        if (category === 'none' || step === 'billing' || step === 5) {
          const finalResult = {
            mode: 'cups',
            value: helperCupsValue,
            category,
            step,
            addressData: addressResult.data,
            addressId: addressId,
            cups: helperCupsValue,
            niceAddress: addressResult.data.NICE_ADDRESS,
          }

          if (onFinish) {
            onFinish(finalResult)
          }
          setIsLoadingVerticals(false)
          return
        }

        // ========== SUPPLY ADDRESS (con technical info) ==========
        // Para contratos duales, llamar primero a luz y luego a gas
        let gasPricingCalculation = null
        let electricityPricingCalculation = null

        if (energyType === 'gasAndElectricity') {
          // Llamar primero a electricidad
          const electricityTechInfo = await getTechnicalInfo({
            leadId,
            addressId,
            energyType: 'ELECTRICITY',
            productId: selectedProduct?.id ?? selectedProduct.find((elem) => elem.energyType === 'ELECTRICITY')?.id
          })

           // Detectar cambio de titular en electricidad
          if (electricityTechInfo.success && electricityTechInfo.data?.contractType === 'CCT') {
            // Solo setear ownerElectricityAlert si step === 1
            if (step === 1) {
              setOwnerElectricityAlert(true)
            }
            // Si estamos en step 2 y NO somos pymes, abrir modal
            if (step === 3 && showChangeOwnerModal) {
              showChangeOwnerModal('electricity')
            }
          } else {
            if (step === 1) {
              setOwnerElectricityAlert(false)
            }
          }


          if (!electricityTechInfo.success) {
            const errorResult = {
              mode: 'cups',
              value: helperCupsValue,
              category,
              step,
              error: true,
              errorCode: electricityTechInfo.errorCode,
              errorMessage: electricityTechInfo.error,
              errorStatus: electricityTechInfo.status,
            }

            if (onFinish) {
              onFinish(errorResult)
            }
            setIsLoadingVerticals(false)
            return
          }

          // Llamar a gas
          const gasTechInfo = await getTechnicalInfo({
            leadId,
            addressId,
            energyType: 'GAS',
            productId: selectedProduct?.id ?? selectedProduct.find((elem) => elem.energyType === 'GAS')?.id
          })
          
          // Detectar cambio de titular en gas
          if (gasTechInfo.success && gasTechInfo.data?.contractType === 'CCT') {
            // Solo setear ownerGasAlert si step === 1
            if (step === 1) {
              setOwnerGasAlert(true)
            }
            // Si estamos en step 2 y NO somos pymes, abrir modal
            if (step === 3 && showChangeOwnerModal) {
              showChangeOwnerModal('gas')
            }
          } else {
            if (step === 1) {
              setOwnerGasAlert(false)
            }
          }

          if (!gasTechInfo.success) {
            const errorResult = {
              mode: 'cups',
              value: helperCupsValue,
              category,
              step,
              error: true,
              errorCode: gasTechInfo.errorCode,
              errorMessage: gasTechInfo.error,
              errorStatus: gasTechInfo.status,
            }

            if (onFinish) {
              onFinish(errorResult)
            }
            setIsLoadingVerticals(false)
            return
          }

          // Ambos exitosos, construir resultado final
          const finalResult = {
            mode: 'cups',
            value: helperCupsValue,
            category,
            step,
            addressData: addressResult.data,
            addressId: addressResult.data.ADDRESS_ID,
            cups: addressResult.data.CUPS_LUZ,
            cupsGas: addressResult.data.CUPS_GAS,
            niceAddress: addressResult.data.NICE_ADDRESS,
            ownerElectricityAlert,
            ownerGasAlert,
            technicalInfoElectricity: {
              ...electricityTechInfo.data,
              cups: addressResult.data.CUPS_LUZ,
            },
            technicalInfoGas: {
              ...gasTechInfo.data,
              cups: addressResult.data.CUPS_GAS,
            },
          }

          if (onFinish) {
            onFinish(finalResult)
          }
        } else {
          // Contrato simple (solo luz o solo gas)
          const energyTypeUpper = category === 'electricity' ? 'ELECTRICITY' : 'GAS'
          let pricingCalculation = null
          const techInfo = await getTechnicalInfo({
            leadId: leadId ?? null,
            addressId: addressId ?? null,
            energyType: energyTypeUpper,
            step,
            productId: selectedProduct?.id ?? selectedProduct.find((product) => product.energyType?.toUpperCase() === energyTypeUpper)?.id
          })

                // Detectar cambio de titular en contrato simple
        if (techInfo.success && techInfo.data?.contractType === 'CCT') {
          if (energyTypeUpper === 'ELECTRICITY') {
            // Solo setear ownerElectricityAlert si step === 1
            if (step === 1) {
              setOwnerElectricityAlert(true)
              setOwnerGasAlert(false)
            }
            // Si estamos en step 2 y NO somos pymes, abrir modal
            if (step === 3 && showChangeOwnerModal) {
              showChangeOwnerModal('electricity')
            }
          } else {
            // Solo setear ownerGasAlert si step === 1
            if (step === 1) {
              setOwnerGasAlert(true)
              setOwnerElectricityAlert(false)
            }
          }
        } else {
          if (step === 1) {
            setOwnerElectricityAlert(false)
            setOwnerGasAlert(false)
            }
          }
          if (!techInfo.success) {
            const errorResult = {
              mode: 'cups',
              value: helperCupsValue,
              category,
              step,
              error: true,
              errorCode: techInfo.errorCode,
              errorMessage: techInfo.error,
              errorStatus: techInfo.status,
            }

            if (onFinish) {
              onFinish(errorResult)
            }
            setIsLoadingVerticals(false)
            return
          }

          // Construir resultado final con datos técnicos
          const finalResult = {
            mode: 'cups',
            value: helperCupsValue,
            category,
            step,
            addressData: addressResult?.data || tenure,
            addressId: addressResult?.data?.ADDRESS_ID || tenure?.addressId,
            cups: (category === 'electricity' ? addressResult?.data?.CUPS_LUZ || tenure.cups_luz : addressResult?.data?.CUPS_GAS || tenure?.cups.gas),
            niceAddress: addressResult?.data?.NICE_ADDRESS || tenure?.niceAddress,
            ownerElectricityAlert,
            ownerGasAlert,
            technicalInfo: {
              ...techInfo.data,
            cups: (category === 'electricity' ? addressResult?.data?.CUPS_LUZ || tenure.cups_luz : addressResult?.data?.CUPS_GAS || tenure?.cups.gas),
            },
          }

          if (onFinish) {
            onFinish(finalResult)
          }
        }

        setIsLoadingVerticals(false)
      }
    } catch (err) {
      setError('Error al procesar la búsqueda')
      setIsLoadingVerticals(false)
    }
  }

  const handleHelperCupsKeyPress = (event) => {
    if (event.key === 'Enter' && isHelperCupsValid) {
      handleHelperCupsSubmit()
    }
  }

  const handleSubmit = async (payload = {}) => {

    const tenure = payload?.tenure
    const valid = !payload?.isValid ? isValid : payload?.isValid
    if (!valid) {
      return
    }

    setIsLoadingVerticals(true)

    try {
      const apiStep = step === 2 ? 3 : step === 0 ? 0 : 2
      let addressResult = null
      const inputValue = mode === 'cups' ? cupsValue : addressValue
      let gasPricingCalculation = null
      let electricityPricingCalculation = null

      // ========== OBTENER DATOS DE DIRECCIÓN ==========
      // Ambos servicios (CUPS y vertical) devuelven la misma estructura
      if (!tenure || !tenure?.addressId || inputValue !== '') {
        if (mode === 'cups' && cupsValue) {

          addressResult = await searchByCups({
            cups: cupsValue,
            leadId: leadId ?? null,
            category,
            step: apiStep,
            energy: energyType,
          })

        } else if (mode === 'address' && selectedVertical && selectedVertical !== 'no-vertical' && (selectedAddress?.placeId || selectedAddress?.uuid)) {
          const selectedVerticalData = verticals.find(v => v.value === selectedVertical)

          addressResult = await getVerticalDetail({
            leadId: leadId ?? null,
            verticalId: selectedVertical,
            step: apiStep,
            category,
            placeId: selectedAddress?.placeId || selectedAddress?.uuid,
            label: selectedVerticalData?.label || '',
            energy: energyType,
          })

          // 🆕 Comparar CUPS si venimos de un CUPS fallido
          if (initialFailedCups && addressResult?.data?.CUPS_LUZ) {
            const foundCups = addressResult.data.CUPS_LUZ
            const cupsMismatch = !compareCupsLastDigits(initialFailedCups, foundCups)
          }

        } else {
          // No hay suficientes datos para hacer la búsqueda
          setIsLoadingVerticals(false)
          return
        }

        // ========== VALIDAR RESPUESTA ==========
        if (!addressResult || !addressResult.success) {
          // Detectar error 404 (CUPS no encontrado) en modo CUPS
          if (mode === 'cups' && (addressResult?.httpCode === 404 || addressResult?.errorCode === '02')) {
            if (step === -1) {
              showErrorModal()
            }
            setShowCupsNotFoundAlert(true)
            setInitialFailedCups(cupsValue) // Guardar el CUPS que falló
            setIsLoadingVerticals(false)
            return
          }

          if (addressResult?.errorCode) {
            // Devolver error al componente padre para que maneje las modales
            const errorResult = {
              mode,
              value: inputValue,
              category,
              step,
              error: true,
              errorCode: addressResult?.errorCode,
              errorMessage: addressResult?.error,
              errorStatus: addressResult?.status,
              errorCodeResult: addressResult?.errorCodeResult || addressResult?.data?.errorCode
            }

            if (addressResult.errorCode === '11') {

              errorResult.onContinue = () => {
                handleSubmit({ forceAccept: true })
              }
            }

            if (onFinish) {
              onFinish(errorResult)
            }
          } else {
            // Error genérico sin código
            setError(addressResult?.error || 'Error al buscar')
          }

          setIsLoadingVerticals(false)
          return
        }
      }
      // ========== PROCESAR RESULTADO EXITOSO ==========

      const addressId = addressResult?.data?.ADDRESS_ID || tenure?.addressId
      // ========== BILLING ADDRESS (sin technical info) ==========
      // step 5 = billing step (STEPS.billing)
      
      if (category === 'none' || step === 'billing' || step === 5) {
        const finalResult = {
          mode,
          value: inputValue,
          category,
          step,
          addressData: addressResult?.data || tenure,
          addressId: addressId,
          cups: (category === 'electricity' ? addressResult?.data?.CUPS_LUZ || tenure.cups_luz : addressResult?.data?.CUPS_GAS || tenure?.cups.gas),
          niceAddress: addressResult?.data?.NICE_ADDRESS || tenure?.niceAddress,
        }


        if (onFinish) {
          onFinish(finalResult)
        }
        setIsLoadingVerticals(false)
        return
      }

      // ========== SUPPLY ADDRESS (con technical info) ==========

      // Para contratos duales, llamar primero a luz y luego a gas

      if (energyType === 'gasAndElectricity') {

        // Llamar primero a electricidad
        const electricityTechInfo = await getTechnicalInfo({
          leadId,
          addressId,
          energyType: 'ELECTRICITY',
          productId: selectedProduct?.id ?? selectedProduct.find((elem) => elem.energyType === 'ELECTRICITY')?.id
        })

           // Detectar cambio de titular en electricidad
          if (electricityTechInfo.success && electricityTechInfo.data?.contractType === 'CCT') {
            // Solo setear ownerElectricityAlert si step === 1
            if (step === 1) {
              setOwnerElectricityAlert(true)
            }
            // Si estamos en step 2 y NO somos pymes, abrir modal
            if (step === 3 && showChangeOwnerModal) {
              showChangeOwnerModal('electricity')
            }
          } else {
            if (step === 1) {
              setOwnerElectricityAlert(false)
            }
          }


        if (!electricityTechInfo.success) {
          const errorResult = {
            mode,
            value: inputValue,
            category,
            step,
            error: true,
            errorCode: electricityTechInfo.errorCode,
            errorMessage: electricityTechInfo.error,
            errorStatus: electricityTechInfo.status,
          }

          if (onFinish) {
            onFinish(errorResult)
          }
          setIsLoadingVerticals(false)
          return
        }


        // Llamar a gas
        const gasTechInfo = await getTechnicalInfo({
          leadId,
          addressId,
          energyType: 'GAS',
          productId: selectedProduct?.id ?? selectedProduct.find((elem) => elem.energyType === 'GAS')?.id
        })
        
        // Detectar cambio de titular en gas
        if (gasTechInfo.success && gasTechInfo.data?.contractType === 'CCT') {
          // Solo setear ownerGasAlert si step === 1
          if (step === 1) {
            setOwnerGasAlert(true)
          }
          // Si estamos en step 2 y NO somos pymes, abrir modal
          if (step === 3 && showChangeOwnerModal) {
            showChangeOwnerModal('gas')
          }
        } else {
          if (step === 1) {
            setOwnerGasAlert(false)
          }
        }

        if (!gasTechInfo.success) {
          // Verificar si el error es específicamente "CUPS de gas no encontrado" (código 02 o 404)
          const isGasCupsNotFound = gasTechInfo.errorCode === '01'
          
          if (isGasCupsNotFound) {
            // Si no hay CUPS de gas, continuar solo con electricidad
            // y permitir entrada manual de gas después
            console.log('Gas CUPS not found, proceeding with electricity flow only')
            
            const finalResult = {
              mode,
              value: inputValue,
              category: 'electricity', // Cambiar a electricity para procesamiento correcto
              step,
              addressData: addressResult.data,
              addressId: addressResult.data.ADDRESS_ID,
              cups: addressResult.data.CUPS_LUZ,
              niceAddress: addressResult.data.NICE_ADDRESS,
              allowManualGasCups: true, // Flag para habilitar entrada manual de gas
              originalSupplyType: 'gasAndElectricity', // Mantener intención original
              technicalInfoElectricity: isFlatRateElectricity ? {
                ...electricityTechInfo.data,
                ...electricityPricingCalculation.data,
                cups: addressResult.data.CUPS_LUZ,
              } : {
                ...electricityTechInfo.data,
                cups: addressResult.data.CUPS_LUZ,
              },
            }

            if (onFinish) {
              onFinish(finalResult)
            }
            setIsLoadingVerticals(false)
            return
          } else {
            // Cualquier otro error de gas, devolver error como antes
            const errorResult = {
              mode,
              value: inputValue,
              category,
              step,
              error: true,
              errorCode: gasTechInfo.errorCode,
              errorMessage: gasTechInfo.error,
              errorStatus: gasTechInfo.status,
              technicalInfo: {
                ...gasTechInfo?.technicalInfo,
                cups:addressResult?.data?.cups_gas,
                addressId,
                niceAddress:addressResult?.data?.nice_address}
            }

            if (onFinish) {
              onFinish(errorResult)
            }
            setIsLoadingVerticals(false)
            return
          }
        }

        // 🆕 Verificar si hay mismatch de CUPS
        const cupsMismatch = initialFailedCups && !compareCupsLastDigits(initialFailedCups, addressResult.data.CUPS_LUZ)

        // Ambos exitosos, construir resultado final
        const finalResult = {
          mode,
          value: inputValue,
          category,
          step,
          addressData: addressResult.data,
          addressId: addressResult.data.ADDRESS_ID,
          cups: addressResult.data.CUPS_LUZ,
          cupsGas: addressResult.data.CUPS_GAS,
          niceAddress: addressResult.data.NICE_ADDRESS,
          cupsMismatch, // 🆕 Indicar si hay discrepancia de CUPS
          initialFailedCups: cupsMismatch ? initialFailedCups : undefined, // 🆕 CUPS original que falló
          technicalInfoElectricity: {
            ...electricityTechInfo.data,
            cups: addressResult.data.CUPS_LUZ,
          },
          technicalInfoGas: {
            ...gasTechInfo.data,
            cups: addressResult.data.CUPS_GAS,
          },
        }
        // Incluir flags de cambio de titular
        finalResult.ownerElectricityAlert = ownerElectricityAlert
        finalResult.ownerGasAlert = ownerGasAlert


        if (onFinish) {
          onFinish(finalResult)
        }
      } else {
        // Contrato simple (solo luz o solo gas)
        const energyTypeUpper = category === 'electricity' ? 'ELECTRICITY' : 'GAS'
        let pricingCalculation = null
        const techInfo = await getTechnicalInfo({
          leadId: leadId ?? null,
          addressId: addressId ?? null,
          energyType: energyTypeUpper,
          step,
          productId: selectedProduct?.id ?? selectedProduct.find((product) => product.energyType?.toUpperCase() === energyTypeUpper)?.id
        })

                // Detectar cambio de titular en contrato simple
        if (techInfo.success && techInfo.data?.contractType === 'CCT') {
          if (energyTypeUpper === 'ELECTRICITY') {
            // Solo setear ownerElectricityAlert si step === 1
            if (step === 1) {
              setOwnerElectricityAlert(true)
              setOwnerGasAlert(false)
            }
            // Si estamos en step 2 y NO somos pymes, abrir modal
            if (step === 3 && showChangeOwnerModal) {
              showChangeOwnerModal('electricity')
            }
          } else {
            // Solo setear ownerGasAlert si step === 1
            if (step === 1) {
              setOwnerGasAlert(true)
              setOwnerElectricityAlert(false)
            }
          }
        } else {
          if (step === 1) {
            setOwnerElectricityAlert(false)
            setOwnerGasAlert(false)
          }
        }
        if (!techInfo.success) {
          const errorResult = {
            mode,
            value: inputValue,
            category,
            step,
            error: true,
            errorCode: techInfo.errorCode,
            errorMessage: techInfo.error,
            errorStatus: techInfo.status,
            technicalInfo: {
              ...techInfo?.technicalInfo,
              cups:addressResult?.data?.cups_luz || addressResult?.data?.cups_gas,
              addressId,
              niceAddress:addressResult?.data?.nice_address}
          }

          if (onFinish) {
            onFinish(errorResult)
          }
          setIsLoadingVerticals(false)
          return
        }


        // 🆕 Verificar si hay mismatch de CUPS (solo para búsqueda por dirección)
        const foundCups = category === 'electricity' ? addressResult?.data?.CUPS_LUZ : addressResult?.data?.CUPS_GAS
        const cupsMismatch = mode === 'address' && initialFailedCups && foundCups && !compareCupsLastDigits(initialFailedCups, foundCups)

        // Construir resultado final con datos técnicos
        const finalResult = {
          mode,
          value: inputValue,
          category,
          step,
          addressData: addressResult?.data || tenure,
          addressId: addressResult?.data?.ADDRESS_ID || tenure?.addressId,
          cups: (category === 'electricity' ? addressResult?.data?.CUPS_LUZ || tenure.cups_luz : addressResult?.data?.CUPS_GAS || tenure?.cups.gas),
          niceAddress: addressResult?.data?.NICE_ADDRESS || tenure?.niceAddress,
          cupsMismatch, // 🆕 Indicar si hay discrepancia de CUPS
          initialFailedCups: cupsMismatch ? initialFailedCups : undefined, // 🆕 CUPS original que falló
          ownerElectricityAlert,
          ownerGasAlert,
          technicalInfo: {
            ...techInfo.data,
            cups: (category === 'electricity' ? addressResult?.data?.CUPS_LUZ || tenure.cups_luz : addressResult?.data?.CUPS_GAS || tenure?.cups.gas),
          },
        }


        if (onFinish) {
          onFinish(finalResult)
        }
      }

      setIsLoadingVerticals(false)
    } catch (err) {
      setError('Error al procesar la búsqueda')
      setIsLoadingVerticals(false)
    }
  }
  // Efecto para lanzar la búsqueda automáticamente a los 500ms de escribir el número
  useEffect(() => {
    if (!showNumberField || !numberValue || numberValue.trim() === '') {
      return
    }

    const timer = setTimeout(() => {
      handleNumberSubmit()
    }, 500)

    return () => clearTimeout(timer)
  }, [numberValue])

  // Función de envío del número con detección del error 12
 const handleNumberSubmit = async () => {
  
    if (!numberValue || !addressWithoutNumber) return

    isProcessingNumberRef.current = true; // Bloquear eventos de input externos

    const updatedDescription = addressWithoutNumber.description.replace(',', `, ${numberValue},`)

    const updatedAddress = {
      ...addressWithoutNumber,
      description: updatedDescription,
    }

    setIsLoadingVerticals(true)
    setShowAddressSearchError(false)

    try {
      const apiStep = step === 2 ? 3 : step === 0 ? 0 : 2
      const result = await getAddressDetails({
        placeId: updatedAddress.placeId,
        uuid: updatedAddress.uuid,
        label: updatedAddress.description,
        category,
        step: apiStep,
      })

      const isHorizontalNotFound = 
        result.data?.results?.code === '12' || 
        result.data?.code === '12' ||
        result.errorCode === '12' ||
        result.results?.code === '12' ||
        (result.data?.status === 'error' && result.data?.results?.message === 'Horizontal Not Found')

      if (!result.success || isHorizontalNotFound) {
        setSelectedAddress(null)
        setIsValid(false)
        setShowAddressSearchError(true)

        if (shouldShowCupsSearch) {
          setShowCupsHelper(true)
          setHelperCupsValue(cupsValue)
          setIsHelperCupsValid(false)
          setHelperCupsSearchFailed(false)
        }
      } else {
        setShowAddressSearchError(false)
        setShowCupsHelper(false)
        
        const verticalsList = result.data?.verticals || result.data?.results || result.results

        if (verticalsList && verticalsList.length > 0) {
          setSelectedAddress(updatedAddress)
          setVerticals(verticalsList)
          setVerticalsDetail({
            ...result.data,
            placeIDGoogle: updatedAddress?.placeIDGoogle || updatedAddress?.placeId,
            description: result.data?.niceAddress || updatedAddress.description,
          })
          // NO ocultar el campo de número - mantenerlo visible

          if (verticalsList.length === 1) {
            const vertical = verticalsList[0]
            const hasAnyField = vertical.building || vertical.staircase || vertical.floor || vertical.door

            if (!hasAnyField && vertical.value) {
              setSelectedVertical(vertical.value)
              setIsValid(true)
            } else {
              setIsValid(false)
            }
          } else {
            setIsValid(false)
          }
        } else {
          setSelectedAddress(updatedAddress)
          // NO ocultar el campo de número - mantenerlo visible
          setIsValid(true)
        }
      }
    } catch (err) {
      setSelectedAddress(null)
      setShowAddressSearchError(true)
      if (shouldShowCupsSearch) {
        setShowCupsHelper(true)
      }
      setIsValid(false)
    } finally {
      setIsLoadingVerticals(false)
      // Liberar el bloqueo un breve instante después para estabilizar el DOM
      setTimeout(() => {
        isProcessingNumberRef.current = false;
      }, 300);
    }
  }
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && isValid) {
      handleSubmit()
    }
  }

  // Limpiar timers al desmontar el componente
  useEffect(() => {
    return () => {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current)
      }
      if (noResultsTimerRef.current) {
        clearTimeout(noResultsTimerRef.current)
      }
    }
  }, [])

  return (
    <Box>
      {shouldShowCupsSearch && mode === 'cups' ? (
        // Vista de búsqueda por CUPS
        <Box>
          <Box>
            <Typography variant="h6" className={classes.sectionTitle}>
            {translations.cups.title}
            </Typography>

            <Box className={classes.titleContainer}>
              <Typography variant="body1" className={classes.sectionSubtitle}>
              {translations.cups.subtitle}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  if (openCupsModal) {
                    openCupsModal()
                  }
                }}
                className={classes.infoButton}
              >
                <InfoOutlinedIcon className={classes.infoIcon} />
              </IconButton>
            </Box>
          </Box>

          <TextField
            fullWidth
            variant="filled"
            label={translations.cups.placeholder}
            value={cupsValue}
            onChange={handleCupsChange}
            onBlur={handleCupsBlur}
            onKeyPress={handleKeyPress}
            error={!!error}
            helperText={error || ' '}
            className={error ? classes.textFieldError : classes.textField}
          />

          {/* Alerta amarilla cuando el CUPS no se encuentra (404) */}
          {/* Alerta amarilla cuando el CUPS no se encuentra (404) */}
          {showCupsNotFoundAlert && (
            <Alert
              severity="warning"
              icon={<InfoOutlinedIcon className={classes.helperAlertIcon} />}
              className={classes.helperAlert}
            >
              <Typography className={classes.helperAlertText}>
                <Trans i18nKey="addressSearch.warning.invalidCups">
                  <Link
                    underline="always"
                    onClick={() => {
                      setShowCupsNotFoundAlert(false)
                      handleModeToggle()
                    }}
                    style={{ cursor: 'pointer' }}
                  ></Link>
                </Trans>
              </Typography>
            </Alert>
          )}

          {showModeToggle && (
            <Typography variant="body2" className={classes.toggleText}>
              {translations.cups.toggleText}{' '}
              <Link component="button" onClick={handleModeToggle} className={classes.toggleLink} style={{ cursor: 'pointer' }}>
                {translations.cups.toggleLink}
              </Link>
            </Typography>
          )}

          <Box className={classes.buttonContainer}>
            <Button variant="contained" onClick={handleSubmit} disabled={!isValid} className={classes.submitButton}>
              {translations.cups.button}
            </Button>
          </Box>
        </Box>
      ) : (
        // Vista de búsqueda por dirección
        <Box>
          {!hideTitle && (
            <>
              <Typography variant="h6" className={classes.sectionTitle}>
                {translations.address.title}
              </Typography>

              <Typography variant="body1" className={classes.sectionSubtitle}>
                {translations.address.subtitle}
              </Typography>
            </>
          )}

          <Box className={showNumberField ? classes.addressInputRow : ''}>
          <Autocomplete
            fullWidth={!showNumberField}
            freeSolo
            open={isDropdownOpen && addressValue.length >= 3}
            onOpen={() => {
              if (addressValue.length >= 3) {
                setIsDropdownOpen(true)
              }
            }}
            onClose={() => {
              setIsDropdownOpen(false)
            }}
            componentsProps={{
              paper: {
                className: {
                  '& .MuiAutocomplete-listbox': {
                    '& .MuiAutocomplete-option': {
                      minHeight: '40px !important',
                      padding: '8px 12px !important',
                    },
                  },
                },
              },
            }}
            options={
              isLoadingAddresses
                ? ['Buscando...']
                : showNoResultsOption && addressOptions.length === 0 && addressValue.length >= 3 && !isLoadingAddresses
                  ? [{ isNoResultsOption: true, description: 'No encuentro mi dirección.' }]
                  : addressOptions.length > 0
                    ? addressOptions
                    : []
            }
            value={selectedAddress}
            inputValue={addressValue}
            onInputChange={handleAddressInputChange}
            onChange={handleAddressSelect}
            loading={false}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option
              return option?.description || option?.formatted_address || ''
            }}
            noOptionsText="Escribe al menos 3 caracteres"
            loadingText="Buscando..."
            filterOptions={(options) => options}
            renderOption={(option) => {
              // Si es la opción "No encuentro mi dirección", mostrar con icono
              if (option?.isNoResultsOption) {
                return (
                  <Box className={classes.optionContainer}>
                    <LocationOnOutlined className={classes.optionIcon} />
                    <Typography>{option.description}</Typography>
                  </Box>
                )
              }
              // Opciones normales con icono de ubicación
              return (
                <Box className={classes.optionContainer}>
                  <LocationOnOutlined className={classes.optionIcon} />
                  <Typography>{option?.description || option?.formatted_address || option}</Typography>
                </Box>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="filled"
                label={translations.address.placeholder}
                InputLabelProps={params.InputLabelProps}
                error={!!error}
                helperText={error}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingAddresses ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                className={error ? classes.addressTextFieldError : classes.addressTextField}
              />
            )}
            className={showNumberField ? classes.addressAutocompleteWithNumber : classes.autocompleteContainer}
          />
          {/* Campo de número en la misma línea */}
            {showNumberField && (
              <TextField
                value={numberValue}
                onChange={handleNumberChange}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && numberValue) {
                    handleNumberSubmit()
                  }
                }}
                label="Nº"
                variant="filled"
                className={classes.numberTextField}
                InputLabelProps={{
                  shrink: undefined,
                }}
              />
            )}
          </Box>

          {/* Alerta de error cuando falla la búsqueda de dirección en billing step */}
          {!shouldShowCupsSearch && showAddressSearchError && (
            <Alert
              severity="warning"
              icon={<InfoOutlinedIcon className={classes.helperAlertIcon} />}
              className={classes.helperAlert}
            >
              <Typography className={classes.helperAlertText}>
                <Trans i18nKey="addressSearch.warning.warningNewAddress">
                  <Link
                    underline="always"
                    onClick={() => {
                      if (onFinish) {
                        onFinish({
                          mode: 'manual',
                          cups: helperCupsValue,
                          category,
                          step,
                          requiresManualAddress: true,
                          verticalsDetail,
                        })
                      }
                    }}
                    className={classes.alertLink}
                  />
                </Trans>
              </Typography>
            </Alert>
          )}

          {/* Selector de verticales (piso, puerta, etc.) */}
          {selectedAddress && verticals.length > 0 && (
            <Box className={classes.autocompleteVerticals}>
              <Autocomplete
                fullWidth
                options={[...verticals, { isNoVerticalOption: true, label: 'No encuentro mi piso', value: 'no-vertical' }]}
                value={
                  selectedVertical === 'no-vertical'
                    ? { isNoVerticalOption: true, label: 'No encuentro mi piso', value: 'no-vertical' }
                    : verticals.find(v => v.value === selectedVertical) || null
                }
                componentsProps={classes.autocompleteComponentsProps}
                onChange={(event, newValue) => {

                  // Si selecciona "No encuentro mi piso"
                  if (newValue?.isNoVerticalOption) {
                    setSelectedVertical('no-vertical')
                    setIsValid(false)
                    // Solo mostrar el helper de CUPS si shouldShowCupsSearch es true
                    if (shouldShowCupsSearch) {
                    setShowCupsHelper(true)
                    }
                    return
                  }

                  // Solo guardar la vertical seleccionada
                  if (newValue?.value) {
                    setSelectedVertical(newValue.value)
                    setIsValid(true) // Habilitar el botón
                    setShowCupsHelper(false)
                    setError('')
                    // El useEffect se encargará de llamar a handleSubmit si autoSubmit=true
                  } else {
                    setSelectedVertical('')
                    setIsValid(false)
                  }
                }}
                getOptionLabel={(option) => {
                  // Si el label está vacío, mostrar "Dirección única"
                  if (!option.label || option.label.trim() === '') {
                    return 'Dirección única'
                  }
                  return option.label
                }}
                disabled={isLoadingVerticals}
                renderOption={(option) => {
                  // Si es la opción "No encuentro mi piso"
                  if (option?.isNoVerticalOption) {
                    return (
                      <Box className={classes.optionContainer}>
                        <LocationOnOutlined className={classes.optionIcon} />
                        <Typography className={classes.optionText}>No encuentro mi piso</Typography>
                      </Box>
                    )
                  }
                  // Opciones normales de verticales - mostrar con valores en negrita
                  // Si todos los campos están vacíos, mostrar "Dirección única"
                  const hasAnyField = option.building || option.staircase || option.floor || option.door

                  return (
                    <Box className={classes.optionContainer}>
                      <Apartment className={classes.optionIcon} />
                      <Typography className={classes.optionText}>
                        {!hasAnyField ? (
                          'Dirección única'
                        ) : (
                          <>
                        {option.building && (
                          <>Edif. <Box component="span" className={classes.optionTextBold}>{option.building}</Box>, </>
                        )}
                        {option.staircase && (
                          <>Esc. <Box component="span" className={classes.optionTextBold}>{option.staircase}</Box>, </>
                        )}
                        {option.floor && (
                          <>Piso <Box component="span" className={classes.optionTextBold}>{option.floor}</Box>, </>
                        )}
                        {option.door && (
                          <>Prta. <Box component="span" className={classes.optionTextBold}>{option.door}</Box></>
                            )}
                          </>
                        )}
                      </Typography>
                    </Box>
                  )
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    label="Piso, puerta (bloque, escalera...)"
                    InputLabelProps={params.InputLabelProps}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingVerticals ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    className={classes.addressTextField}
                  />
                )}
              />
            </Box>
          )}

          {/* Input de CUPS cuando selecciona "No encuentro mi dirección" o viene desde error 404 */}
          {showCupsHelper && (
            <Box className={classes.helperContainer}>
              <TextField
                fullWidth
                variant="filled"
                label={translations.cups.placeholder}
                value={helperCupsValue}
                onChange={handleHelperCupsChange}
                onKeyPress={handleHelperCupsKeyPress}
                className={classes.helperTextField}
              />

              {/* Alerta informativa */}
              {/* Mostrar alerta de error solo si la búsqueda falló */}
              {showAddressSearchError && helperCupsValue ? (
                <Alert
                  severity="warning"
                  icon={<InfoOutlinedIcon className={classes.helperAlertIcon} />}
                  className={classes.helperAlert}
                >
                  <Typography className={classes.helperAlertText}>
                    <Trans i18nKey="addressSearch.warning.warningNewAddress">
                      <Link
                        underline="always"
                        onClick={() => {
                          if (onFinish) {
                            onFinish({
                              mode: 'manual',
                              cups: helperCupsValue,
                              category,
                              step,
                              requiresManualAddress: true,
                              verticalsDetail,
                            })
                          }
                        }}
                        className={classes.alertLink}
                        style={{ cursor: 'pointer' }}
                      />
                    </Trans>
                  </Typography>
                </Alert>
              ) : !helperCupsValue ? (
                <Alert
                  severity="warning"
                  icon={<InfoOutlinedIcon className={classes.helperAlertIcon} />}
                  className={classes.helperAlert}
                >
                  <Typography className={classes.helperAlertText}>
                    {t('addressSearch.warning.warningAddress')}
                  </Typography>
                </Alert>
              ) : null}

              {/* Botón para comprobar el CUPS del helper */}
              <Box className={classes.buttonContainer}>
                <Button
                  variant="contained"
                  onClick={handleHelperCupsSubmit}
                  disabled={!isHelperCupsValid || isLoadingVerticals}
                  className={classes.submitButton}
                >
                  {isLoadingVerticals ? <CircularProgress size={24} color="inherit" /> : translations.cups.button}
                </Button>
              </Box>
            </Box>
          )}

          {/* Texto "Si lo prefieres puedes introducir el CUPS" - FUERA del helper */}
          {shouldShowCupsSearch && showModeToggle && (
            <Typography variant="body2" className={classes.toggleText}>
              {translations.address.toggleText}{' '}
              <Link component="button" onClick={handleModeToggle} className={classes.toggleLink} style={{ cursor: 'pointer' }}>
                {translations.address.toggleLink}
              </Link>
            </Typography>
          )}

          {/* Solo mostrar botón principal si no está en modo helper CUPS y no es autoSubmit */}
          {!autoSubmit && !showCupsHelper && (
            <Box className={classes.buttonContainer}>
              <Button variant="contained" onClick={handleSubmit} disabled={!isValid} className={classes.submitButton}>
                {translations.address.button}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default AddressSearch
